// Pure math for the retirement calculator.
// All amounts are in the user's chosen currency (no FX). Time unit is years;
// contributions sum annually (monthly * 12) and apply at year-end after growth.
import { CUSTOM_RATE_CODE, getRate } from "@/content/dividend-tax-rates";

/** Resolve the effective withdrawal tax rate (0-1) from inputs. */
export function resolveTaxRate(inp: Pick<RetirementInputs, "countryCode" | "customTaxRatePct">): number {
  if (inp.countryCode === CUSTOM_RATE_CODE) {
    const v = inp.customTaxRatePct ?? 0;
    return Math.min(0.95, Math.max(0, v / 100));
  }
  if (!inp.countryCode) return 0;
  return getRate(inp.countryCode).rate;
}


export interface RetirementInputs {
  currentAge: number;
  targetAge: number;
  currentSavings: number;
  monthlyContribution: number;
  monthlyExpenses: number;       // today's dollars, per month (after-tax spend)
  annualReturnPct: number;       // e.g. 7 for 7%
  inflationPct: number;          // e.g. 2.5
  shariahMode: boolean;          // applies Zakat drag (~2.58% solar)
  // Tax: model "withdrawal tax only" — accumulation is untouched, target is
  // grossed up when the user wants to see what they need *before tax* so the
  // 4% withdrawal nets the desired monthly spending.
  countryCode?: string;          // ISO-2 or "CUSTOM" — drives taxRatePct
  customTaxRatePct?: number;     // used when countryCode === "CUSTOM"
  showAfterTax?: boolean;        // true (default) = target NOT grossed up
}

// Compute the today's-$ nest-egg target with optional pre-tax gross-up.
export function nestEggTargetFor(
  annualExpenses: number,
  taxRateDecimal: number,
  showAfterTax: boolean,
): number {
  const baseTarget = annualExpenses * 25;
  if (showAfterTax) return baseTarget;
  const t = Math.min(0.95, Math.max(0, taxRateDecimal));
  return baseTarget / (1 - t);
}


export interface ProjectionRow {
  age: number;
  yearsFromNow: number;
  nominal: number;
  real: number;             // nominal deflated to today's $
  targetNominal: number;    // nest egg target inflated to that year
  targetToday: number;      // 25 * annualExpenses (constant, today's $)
  // Zakat / Nisab transparency (populated only when shariahMode is on)
  nisabNominal?: number;    // 85g gold × projected gold price that year
  zakatApplied?: boolean;   // true only when balance ≥ Nisab → Zakat deducted
  zakatPaidNominal?: number; // amount removed from the portfolio that year
}

export interface ProjectionResult {
  rows: ProjectionRow[];
  nestEggTargetToday: number;
  yearsToRetire: number;
  projectedAtRetirementNominal: number;
  projectedAtRetirementReal: number;
  targetAtRetirementNominal: number;
  surplusOrGapReal: number;
  onTrack: boolean;
}

// Solar-year-adjusted Zakat: 2.5% per lunar year × (365.25 / 354.367 days).
// Applied yearly in a solar calendar projection.
export const ZAKAT_RATE = 0.025 * (365.25 / 354.367); // ≈ 0.02577

// ---- Nisab (Shariah threshold) ----
// Per AAOIFI: Zakat is only due once wealth ≥ 85 g of gold (the gold Nisab).
// Edit these to retune. Price is in the user's display currency (we assume
// EUR ≈ USD parity for this planner — the relative threshold is what matters).
export const NISAB_GOLD_GRAMS = 85;
export const GOLD_PRICE_PER_GRAM_TODAY = 95;      // ~ spot gold per gram, 2026
export const GOLD_ANNUAL_GROWTH_PCT = 6;          // long-run gold appreciation

export const MAX_HORIZON_AGE = 95;
export const DEFAULT_HORIZON_PAD = 12; // show targetAge + 12 years by default

export function nisabValueAtYear(yearsFromNow: number) {
  const g = GOLD_ANNUAL_GROWTH_PCT / 100;
  return NISAB_GOLD_GRAMS * GOLD_PRICE_PER_GRAM_TODAY * Math.pow(1 + g, yearsFromNow);
}

const safe = (n: unknown, fallback = 0) =>
  typeof n === "number" && Number.isFinite(n) ? n : fallback;

export function annualExpensesFromMonthly(monthlyExpenses: number) {
  return Math.max(0, safe(monthlyExpenses)) * 12;
}

function emptyResult(nestEggTargetToday: number, currentAge: number): ProjectionResult {
  const row: ProjectionRow = {
    age: currentAge,
    yearsFromNow: 0,
    nominal: 0,
    real: 0,
    targetNominal: nestEggTargetToday,
    targetToday: nestEggTargetToday,
  };
  return {
    rows: [row],
    nestEggTargetToday,
    yearsToRetire: 0,
    projectedAtRetirementNominal: 0,
    projectedAtRetirementReal: 0,
    targetAtRetirementNominal: nestEggTargetToday,
    surplusOrGapReal: -nestEggTargetToday,
    onTrack: false,
  };
}

export function projectRetirement(
  inp: RetirementInputs,
  horizonEndAge?: number,
): ProjectionResult {
  const currentAge = Math.max(0, Math.floor(safe(inp.currentAge, 0)));
  const targetAge = Math.max(currentAge, Math.floor(safe(inp.targetAge, currentAge)));
  const currentSavings = Math.max(0, safe(inp.currentSavings));
  const monthlyContribution = Math.max(0, safe(inp.monthlyContribution));
  const monthlyExpenses = Math.max(0, safe(inp.monthlyExpenses));
  const r = safe(inp.annualReturnPct) / 100;
  const i = safe(inp.inflationPct) / 100;
  const shariahMode = !!inp.shariahMode;

  const annualExpenses = monthlyExpenses * 12;
  const showAfterTax = inp.showAfterTax !== false; // default true
  const taxRate = resolveTaxRate(inp);
  const nestEggTargetToday = nestEggTargetFor(annualExpenses, taxRate, showAfterTax);
  const yearsToRetire = Math.max(0, targetAge - currentAge);


  if (yearsToRetire <= 0) return emptyResult(nestEggTargetToday, currentAge);

  const padTarget = (horizonEndAge ?? targetAge + DEFAULT_HORIZON_PAD);
  const endAge = Math.min(MAX_HORIZON_AGE, Math.max(targetAge, padTarget));
  const horizon = Math.max(1, endAge - currentAge);

  const rows: ProjectionRow[] = [];
  let balance = currentSavings;

  rows.push({
    age: currentAge,
    yearsFromNow: 0,
    nominal: balance,
    real: balance,
    targetNominal: nestEggTargetToday,
    targetToday: nestEggTargetToday,
  });

  for (let y = 1; y <= horizon; y++) {
    const age = currentAge + y;
    const stillContributing = age <= targetAge;
    balance = balance * (1 + r) + (stillContributing ? monthlyContribution * 12 : 0);

    // Zakat is only owed once wealth ≥ Nisab (85 g of gold), with the gold
    // price projected forward each year. Below threshold → no deduction.
    let nisabNominal: number | undefined;
    let zakatApplied = false;
    let zakatPaidNominal = 0;
    if (shariahMode) {
      nisabNominal = nisabValueAtYear(y);
      if (balance >= nisabNominal) {
        zakatPaidNominal = balance * ZAKAT_RATE;
        balance -= zakatPaidNominal;
        zakatApplied = true;
      }
    }
    if (!Number.isFinite(balance)) balance = 0;

    const realFactor = 1 / Math.pow(1 + i, y);
    rows.push({
      age,
      yearsFromNow: y,
      nominal: balance,
      real: balance * realFactor,
      targetNominal: nestEggTargetToday * Math.pow(1 + i, y),
      targetToday: nestEggTargetToday,
      nisabNominal,
      zakatApplied: shariahMode ? zakatApplied : undefined,
      zakatPaidNominal: shariahMode ? zakatPaidNominal : undefined,
    });
  }

  const atRetire = rows[Math.min(yearsToRetire, rows.length - 1)];
  return {
    rows,
    nestEggTargetToday,
    yearsToRetire,
    projectedAtRetirementNominal: atRetire.nominal,
    projectedAtRetirementReal: atRetire.real,
    targetAtRetirementNominal: atRetire.targetNominal,
    surplusOrGapReal: atRetire.real - nestEggTargetToday,
    onTrack: atRetire.real >= nestEggTargetToday,
  };
}

// ---------- Solvers ----------

/**
 * Solve for the monthly contribution required so that the *real* balance at
 * retirement equals the nest-egg target (in today's $). Returns the monthly
 * dollar amount (≥ 0), or Infinity if impossible within horizon.
 */
export function solveRequiredMonthlyContribution(inp: RetirementInputs): number {
  // Solver only needs to project up to the retirement age — keep it cheap.
  const project = (m: number) =>
    projectRetirement({ ...inp, monthlyContribution: m }, inp.targetAge);

  const zero = project(0);
  if (zero.projectedAtRetirementReal >= zero.nestEggTargetToday) return 0;

  let lo = 0;
  let hi = Math.max(1000, safe(inp.monthlyExpenses) * 4);
  for (let i = 0; i < 30; i++) {
    const p = project(hi);
    if (p.projectedAtRetirementReal >= p.nestEggTargetToday) break;
    hi *= 2;
    if (hi > 1e9) return Infinity;
  }

  for (let i = 0; i < 40; i++) {
    const mid = (lo + hi) / 2;
    const p = project(mid);
    if (p.projectedAtRetirementReal >= p.nestEggTargetToday) hi = mid;
    else lo = mid;
  }
  return Math.ceil(hi / 10) * 10;
}

/**
 * Find the earliest retirement age at which the projection meets the target
 * without changing other inputs. Returns null if never reached by age 95.
 */
export function solveEarliestRetireAge(inp: RetirementInputs): number | null {
  const horizon = projectRetirement(
    { ...inp, targetAge: MAX_HORIZON_AGE },
    MAX_HORIZON_AGE,
  );
  for (const row of horizon.rows) {
    if (row.age <= inp.currentAge) continue;
    if (row.real >= horizon.nestEggTargetToday) return row.age;
  }
  return null;
}

/**
 * Today's-$ balance the user should have by `milestoneAge` to stay on a glide
 * path that reaches the nest-egg target by `targetAge`, given they continue
 * contributing `monthlyContribution` until retirement.
 */
export function milestoneRealBalance(
  inp: RetirementInputs,
  milestoneAge: number,
): number | null {
  if (milestoneAge <= inp.currentAge || milestoneAge > inp.targetAge) return null;
  const showAfterTax = inp.showAfterTax !== false;
  const target = nestEggTargetFor(
    annualExpensesFromMonthly(inp.monthlyExpenses),
    resolveTaxRate(inp),
    showAfterTax,
  );
  const r = safe(inp.annualReturnPct) / 100;
  const i = safe(inp.inflationPct) / 100;
  let realReturn = (1 + r) / (1 + i) - 1;
  if (inp.shariahMode) realReturn = (1 + realReturn) * (1 - ZAKAT_RATE) - 1;
  const yearsRemaining = inp.targetAge - milestoneAge;
  const growth = Math.pow(1 + realReturn, yearsRemaining);
  if (!Number.isFinite(growth) || growth <= 0) return null;
  const annualContrib = Math.max(0, safe(inp.monthlyContribution)) * 12;
  let pvContribs = 0;
  if (annualContrib > 0 && yearsRemaining > 0) {
    if (Math.abs(realReturn) < 1e-9) {
      pvContribs = annualContrib * yearsRemaining;
    } else {
      pvContribs =
        annualContrib * (1 - Math.pow(1 + realReturn, -yearsRemaining)) / realReturn;
    }
  }
  return Math.max(0, target / growth - pvContribs);
}
